import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  createDateTimeString,
  extractTimeComponents,
  formatForDisplay,
  hasTimeComponent,
  isISOString,
} from "../utils/dateUtils";
import CalendarBlankThinIcon from "./icons/CalendarBlankThinIcon";

interface DatePickerProps {
  value: string; // YYYY-MM-DD or YYYY-MM-DDTHH:mm format
  onChange: (date: string) => void;
  placeholder?: string;
  className?: string;
  includeTime?: boolean;
}

const DatePicker = ({
  value,
  onChange,
  placeholder = "Select date",
  className = "",
  includeTime = false,
}: DatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [selectedPeriod, setSelectedPeriod] = useState("AM");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Create date in local timezone to avoid timezone issues
  const selectedDate = (() => {
    if (!value) return new Date();

    try {
      const date = new Date(value);
      return isNaN(date.getTime()) ? new Date() : date;
    } catch {
      return new Date();
    }
  })();

  // Extract time from value if it includes time
  useEffect(() => {
    if (value && includeTime && hasTimeComponent(value)) {
      const { hour12, minute, period } = extractTimeComponents(value);
      setSelectedTime(
        `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
      );
      setSelectedPeriod(period);
    }
  }, [value, includeTime]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Set current month to selected date when opened
  useEffect(() => {
    if (isOpen && value && isISOString(value)) {
      const date = new Date(value);
      setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    }
  }, [isOpen, value]);

  const getDisplayText = () => {
    const result = formatForDisplay(value, includeTime);
    return result || selectedDate.toLocaleDateString();
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const handleDateSelect = (date: Date) => {
    // Use local date components to avoid timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    if (includeTime) {
      const [hours12, minutes] = selectedTime.split(":");
      const dateOnly = `${year}-${month}-${day}`;
      const isoString = createDateTimeString(
        dateOnly,
        parseInt(hours12),
        parseInt(minutes),
        selectedPeriod
      );
      onChange(isoString);
    } else {
      const isoString = `${year}-${month}-${day}`;
      onChange(isoString);
      setIsOpen(false);
    }
  };

  const handleTimeChange = (time: string, period?: string) => {
    setSelectedTime(time);
    if (period) setSelectedPeriod(period);

    if (selectedDate) {
      const [hours12, minutes] = time.split(":");
      const currentPeriod = period || selectedPeriod;
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const dateOnly = `${year}-${month}-${day}`;
      const isoString = createDateTimeString(
        dateOnly,
        parseInt(hours12),
        parseInt(minutes),
        currentPeriod
      );
      onChange(isoString);
    }
  };

  const handleConfirm = () => {
    setIsOpen(false);
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + (direction === "next" ? 1 : -1));
      return newMonth;
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date) => {
    if (!selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const monthYear = currentMonth.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Input Field */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-transparent border-0 border-b border-gray-300 focus:border-blue-500 focus:ring-0 outline-none px-0 py-2 text-gray-900 cursor-pointer flex items-center justify-between"
      >
        <span className={selectedDate ? "text-gray-900" : "text-gray-500"}>
          {selectedDate ? getDisplayText() : placeholder}
        </span>
        <CalendarBlankThinIcon className="w-5 h-5" color="#9CA3AF" />
      </div>

      {/* Dropdown Calendar */}
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-64">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigateMonth("prev")}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="w-3 h-3" />
            </button>
            <h3 className="text-sm font-medium text-gray-900">{monthYear}</h3>
            <button
              onClick={() => navigateMonth("next")}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>

          {/* Days of week */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
              <div
                key={index}
                className="text-xs font-medium text-gray-500 text-center py-1"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {getDaysInMonth(currentMonth).map((date, index) => {
              if (!date) {
                return <div key={index} className="h-8" />;
              }

              return (
                <button
                  key={index}
                  onClick={() => handleDateSelect(date)}
                  className={`
                    h-6 text-xs rounded hover:bg-gray-100 transition-colors
                    ${
                      isSelected(date)
                        ? "bg-black text-white hover:bg-gray-800"
                        : isToday(date)
                        ? "bg-gray-100 text-gray-900 font-medium"
                        : "text-gray-700"
                    }
                  `}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Time Picker */}
          {includeTime && (
            <div className="border-t border-gray-200 mt-2 pt-2">
              <div className="flex items-center justify-between space-x-2">
                <label className="text-xs font-medium text-gray-700">
                  Time:
                </label>
                <div className="flex items-center space-x-1">
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={parseInt(selectedTime.split(":")[0]) || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      const minutes = selectedTime.split(":")[1];

                      // Allow any typing, validation happens on blur
                      if (value === "") {
                        return;
                      }

                      const numValue = parseInt(value);
                      if (!isNaN(numValue)) {
                        setSelectedTime(
                          `${String(numValue).padStart(2, "0")}:${minutes}`
                        );
                      }
                    }}
                    onBlur={(e) => {
                      const value = parseInt(e.target.value) || 1;
                      const validHour =
                        value > 12 ? 1 : Math.max(1, Math.min(12, value));
                      const minutes = selectedTime.split(":")[1];
                      handleTimeChange(
                        `${String(validHour).padStart(2, "0")}:${minutes}`
                      );
                    }}
                    className="px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-black focus:border-black bg-white w-12 text-center"
                  />
                  <span className="text-xs text-gray-500">:</span>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={parseInt(selectedTime.split(":")[1]) || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      const hours = selectedTime.split(":")[0];
                      if (value === "") {
                        return; // Don't update while typing
                      } else {
                        const numValue = parseInt(value);
                        if (!isNaN(numValue)) {
                          if (numValue >= 60) {
                            setSelectedTime(`${hours}:00`);
                          } else if (numValue >= 0) {
                            setSelectedTime(
                              `${hours}:${String(numValue).padStart(2, "0")}`
                            );
                          }
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const value = parseInt(e.target.value) || 0;
                      const validMinute =
                        value >= 60 ? 0 : Math.max(0, Math.min(59, value));
                      const hours = selectedTime.split(":")[0];
                      handleTimeChange(
                        `${hours}:${String(validMinute).padStart(2, "0")}`
                      );
                    }}
                    className="px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-black focus:border-black bg-white w-12 text-center"
                  />
                  <select
                    value={selectedPeriod}
                    onChange={(e) =>
                      handleTimeChange(selectedTime, e.target.value)
                    }
                    className="px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-black focus:border-black bg-white w-12"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
                <button
                  onClick={handleConfirm}
                  className="bg-black text-white py-1 px-2 rounded text-xs font-medium hover:bg-gray-800 transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
